import type { ProfitAgentDrilldown } from '../../types/profitabilityAgent';
import { maskProfitAgentDrilldownLabel } from '../../utils/profitAgentText';
import { Button } from '@/components/ui/button';

type Row = {
  label: string;
  revenue: number;
  marginPct: number | null;
  profit: number;
};

type ProfitAgentEvidenceStripProps = {
  drilldown: ProfitAgentDrilldown;
  rows: Row[];
  maskEntities?: boolean;
  onOpenFullReport?: () => void;
};

export default function ProfitAgentEvidenceStrip({
  drilldown,
  rows,
  maskEntities = false,
  onOpenFullReport,
}: ProfitAgentEvidenceStripProps) {
  const entityLabel = maskProfitAgentDrilldownLabel(drilldown, maskEntities);
  const title =
    drilldown.type === 'vehicle'
      ? `Evidence — vehicle ${entityLabel}`
      : drilldown.type === 'customer'
        ? `Evidence — customer ${entityLabel}`
        : `Evidence — ${entityLabel}`;

  return (
    <div className="mt-3 rounded-lg border border-border/60 bg-background/50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-xs font-semibold text-primary/90 uppercase tracking-wide">{title}</div>
        {onOpenFullReport ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={onOpenFullReport}
            className="h-auto p-0 text-chart-10 hover:text-chart-10 no-underline hover:underline underline-offset-2"
          >
            Open full Profitability Report
          </Button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No matching rows in the loaded period data.</p>
      ) : (
        <ul className="space-y-1.5 text-xs text-foreground">
          {rows.slice(0, 5).map((r, i) => (
            <li key={i} className="flex flex-wrap gap-x-3 gap-y-0.5">
              <span className="text-muted-foreground">{r.label}</span>
              <span>Revenue £{Math.round(r.revenue).toLocaleString()}</span>
              <span>
                Margin {r.marginPct == null ? '—' : `${r.marginPct.toFixed(1)}%`} (£
                {Math.round(r.profit).toLocaleString()})
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
